// Utilitários para formatação de motivos de drop/reprovação

/**
 * Normaliza `motivo_drop`, que é TEXT no banco: desde 2025-11 guarda um array
 * em JSON (`["Indisponível"]`); registros antigos têm texto simples, e alguns
 * caminhos devolvem literal de array do Postgres (`{a,b}`) ou já um array.
 */
export function parseDropReasons(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseDropReasons(item));
  }
  if (typeof value !== 'string') return [];
  const text = value.trim();
  if (!text) return [];

  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item) => item != null)
          .map((item) => String(item).trim())
          .filter(Boolean);
      }
    } catch {
      // Não é JSON válido: segue como texto simples.
    }
  }

  if (text.startsWith('{') && text.endsWith('}')) {
    return text
      .slice(1, -1)
      .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
      .map((part) => part.trim().replace(/^"|"$/g, '').trim())
      .filter(Boolean);
  }

  return [text];
}

/**
 * Formata os motivos para exibição em lista
 * @param motivos - Array de strings, string única, JSON de array ou literal `{a,b}`
 * @param className - Classes CSS opcionais para a lista
 * @returns JSX com lista formatada ou null se não houver motivos
 */
export function formatMotivosLista(motivos?: unknown, className?: string) {
  const motivosArray = parseDropReasons(motivos);

  if (motivosArray.length === 0) return null;

  if (motivosArray.length === 1) {
    return <span className={className}>{motivosArray[0]}</span>;
  }

  return (
    <ul className={`list-disc list-inside space-y-0.5 ${className || ''}`}>
      {motivosArray.map((motivo, idx) => (
        <li key={idx}>{motivo}</li>
      ))}
    </ul>
  );
}

/**
 * Formata os motivos para exibição inline (separados por vírgula)
 * @param motivos - Array de strings, string única, JSON de array ou literal `{a,b}`
 * @returns String formatada ou string vazia se não houver motivos
 */
export function formatMotivosInline(motivos?: unknown): string {
  return parseDropReasons(motivos).join(', ');
}

/**
 * Verifica se há motivos válidos
 * @param motivos - Array de strings, string única, JSON de array ou literal `{a,b}`
 * @returns boolean indicando se há motivos
 */
export function hasMotivos(motivos?: unknown): boolean {
  return parseDropReasons(motivos).length > 0;
}
